import Moment from 'moment-timezone';
import { TIME_ZONE } from '../config/locale';

// Pinpad black box: one log per sale (by id), last pinpad sales kept in
// localStorage so cashiers can copy it when a payment fails.
const STORAGE_KEY = 'pinpadBlackBox';
const MAX_PINPAD_SALES = 10;
const MAX_EVENTS_PER_SALE = 200;
const MAX_AGE_HOURS = 48;
const MAX_OPEN_SALE_AGE_HOURS = 12;
const PINPAD_EVENT_PREFIXES = ['SALE_', 'VOID_'];

const EMPTY_STORE = { lastSequence: 0, sales: [] };

// used when localStorage is unavailable
let memoryStore = EMPTY_STORE;
let storageUsable = true;
let listeners = [];

const isSale = (sale) =>
  Boolean(sale) &&
  typeof sale.id === 'string' &&
  Number.isInteger(sale.sequence) &&
  Array.isArray(sale.events);

// re-read before every write so tabs don't overwrite each other
const readStore = () => {
  if (!storageUsable) return memoryStore;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || !Array.isArray(parsed.sales)) return EMPTY_STORE;
    return {
      lastSequence: Number.isInteger(parsed.lastSequence)
        ? parsed.lastSequence
        : 0,
      sales: parsed.sales.filter(isSale),
    };
  } catch (error) {
    return memoryStore;
  }
};

const hoursSince = (isoTime) =>
  (Date.now() - new Date(isoTime).getTime()) / (60 * 60 * 1000);

const hasPinpadActivity = (sale) =>
  sale.events.some((event) =>
    PINPAD_EVENT_PREFIXES.some((prefix) => event.type.indexOf(prefix) === 0),
  );

const isOpen = (sale) => !sale.events.some((event) => event.type === 'UNMOUNT');

const pruneSales = (sales, maxPinpadSales = MAX_PINPAD_SALES) => {
  const alive = sales.filter(
    (sale) => hoursSince(sale.startedAt) < MAX_AGE_HOURS,
  );
  const pinpadSales = alive.filter(hasPinpadActivity).slice(-maxPinpadSales);
  const openSales = alive.filter(
    (sale) =>
      !hasPinpadActivity(sale) &&
      isOpen(sale) &&
      hoursSince(sale.startedAt) < MAX_OPEN_SALE_AGE_HOURS,
  );
  return [...pinpadSales, ...openSales].sort((a, b) => a.sequence - b.sequence);
};

// deferred: avoids updating a panel that is unmounting
const notifyListeners = () => {
  setTimeout(() => listeners.forEach((listener) => listener()), 0);
};

const writeStore = (store) => {
  memoryStore = store;
  if (storageUsable) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (error) {
      // quota exceeded: retry smaller, else memory only
      try {
        const reduced = {
          ...store,
          sales: pruneSales(store.sales, Math.floor(MAX_PINPAD_SALES / 2)),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(reduced));
        memoryStore = reduced;
      } catch (retryError) {
        storageUsable = false;
      }
    }
  }
  notifyListeners();
};

export const generatePinpadSaleId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const rowTransactionCode = (row) =>
  row && row.paymentMethod ? row.paymentMethod.transactionCode : null;

export const summarizePaymentRows = (rows = []) =>
  rows
    .map((row, index) => {
      if (!row) return `${index}: -`;
      const reference = row.voucherCode ? row.voucherCode : '-';
      const paid = row.pinpadPaid ? ' paid' : '';
      return `${index}: ${rowTransactionCode(row) || '-'} ${
        row.amount === undefined || row.amount === null ? '-' : row.amount
      } ref=${reference}${paid}`;
    })
    .join(' | ');

const buildSale = (store, { id, form, total, userId }) => ({
  id,
  sequence: store.lastSequence + 1,
  form,
  total: total === undefined ? null : total,
  userId: userId || null,
  path: window.location.pathname,
  startedAt: new Date().toISOString(),
  events: [],
});

export const startPinpadSale = ({ form, total, userId }) => {
  const id = generatePinpadSaleId();
  try {
    const store = readStore();
    const sale = buildSale(store, { id, form, total, userId });
    writeStore({
      lastSequence: sale.sequence,
      sales: [...pruneSales(store.sales), sale],
    });
  } catch (error) {
    // never break a payment
  }
  return id;
};

export const recordPinpadEvent = ({
  saleId,
  form,
  type,
  detail = '',
  rows,
  meta,
}) => {
  try {
    const store = readStore();
    const event = {
      time: new Date().toISOString(),
      form,
      type,
      detail,
      rows: rows ? summarizePaymentRows(rows) : '',
    };
    if (meta) event.meta = meta;

    const existing = store.sales.find((sale) => sale.id === saleId);
    const sale =
      existing ||
      buildSale(store, { id: saleId || generatePinpadSaleId(), form });
    const updatedSale = {
      ...sale,
      events: [...sale.events, event].slice(-MAX_EVENTS_PER_SALE),
    };

    writeStore({
      lastSequence: existing ? store.lastSequence : sale.sequence,
      sales: existing
        ? store.sales.map((current) =>
            current.id === saleId ? updatedSale : current,
          )
        : [...store.sales, updatedSale],
    });
  } catch (error) {
    // never break a payment
  }
};

export const getPinpadSaleEvents = (saleId) => {
  const sale = readStore().sales.find((current) => current.id === saleId);
  return sale ? sale.events : [];
};

export const subscribePinpadEvents = (listener) => {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((current) => current !== listener);
  };
};

const formatTime = (time) =>
  Moment.tz(time, TIME_ZONE).format('DD/MM HH:mm:ss.SSS');

export const formatPinpadEventLine = ({ time, form, type, detail, rows }) =>
  `${formatTime(time)} [${form}] ${type}${detail ? ` ${detail}` : ''}${
    rows ? ` || ${rows}` : ''
  }`;

const eventsOfType = (sale, type) =>
  sale.events.filter((event) => event.type === type);

const summarizeSale = (sale) => {
  const saleResponses = eventsOfType(sale, 'SALE_RESPONSE');
  const approvedReferences = saleResponses
    .filter((event) => event.meta && event.meta.approved)
    .map((event) => event.meta.reference);
  const failedSales =
    saleResponses.length -
    approvedReferences.length +
    eventsOfType(sale, 'SALE_NO_RESPONSE').length +
    eventsOfType(sale, 'SALE_ERROR').length;
  const approvedVoids = eventsOfType(sale, 'VOID_RESPONSE').filter(
    (event) => event.meta && event.meta.approved,
  ).length;
  const failedVoids = eventsOfType(sale, 'VOID_START').length - approvedVoids;
  const unmount = eventsOfType(sale, 'UNMOUNT').pop();

  let submit = 'no registrado';
  if (eventsOfType(sale, 'SUBMIT_START').length) submit = 'sí';
  if (eventsOfType(sale, 'SUBMIT_FAILED').length) submit = 'falló';

  return [
    `pinpad ${approvedReferences.length} aprobada(s)${
      approvedReferences.length ? ` (ref ${approvedReferences.join(', ')})` : ''
    }`,
    `${failedSales} fallida(s)`,
    `${approvedVoids} anulación(es)${
      failedVoids > 0 ? `, ${failedVoids} anulación(es) no hechas` : ''
    }`,
    `envío: ${submit}`,
    `cierre: ${unmount ? formatTime(unmount.time) : 'abierta'}`,
  ].join(' · ');
};

const formatSale = (sale, isCurrent) => [
  `=== Venta #${sale.sequence}${isCurrent ? ' (actual)' : ''} · ${
    sale.form
  } · user ${sale.userId || '-'} · ${sale.path} · inicio ${formatTime(
    sale.startedAt,
  )} · total ${sale.total === null ? '-' : sale.total} ===`,
  `Resumen: ${summarizeSale(sale)}`,
  ...sale.events.map(formatPinpadEventLine),
  '',
];

export const formatPinpadBlackBox = ({ userId, currentSaleId }) => {
  const header = [
    `Flit POS log · user ${userId || '-'}`,
    `Copiado: ${formatTime(new Date().toISOString())} · ${
      window.location.pathname
    }`,
    '',
  ];
  const body = readStore()
    .sales.filter((sale) => hoursSince(sale.startedAt) < MAX_AGE_HOURS)
    .reverse()
    .reduce(
      (lines, sale) => [
        ...lines,
        ...formatSale(sale, sale.id === currentSaleId),
      ],
      [],
    );
  return [...header, ...body].join('\n');
};
